// Onboarding step navigation utility
// Based on the onboarding_step from user profile

export const ONBOARDING_STEPS = {
  EMAIL_REGISTRATION: "email_registration",
  EMAIL_VERIFICATION: "email_verification",
  PHONE_VALIDATION: "phone_validation",
  SUBSCRIPTION: "subscription",
  COMPLETED: "completed",
};

export const SIGNUP_ROUTES = {
  [ONBOARDING_STEPS.EMAIL_REGISTRATION]: "/signup?step=1&mode=emailRegistration",
  [ONBOARDING_STEPS.EMAIL_VERIFICATION]: "/signup?step=1&mode=emailVerification",
  [ONBOARDING_STEPS.PHONE_VALIDATION]: "/signup?step=2&mode=verifyMobile",
  [ONBOARDING_STEPS.SUBSCRIPTION]: "/signup?step=6&mode=verifySubscription",
  [ONBOARDING_STEPS.COMPLETED]: "/onboarding/chat",
};

/**
 * Get the next route based on user's completed onboarding steps
 * @param {Array|string} completedSteps - Array of completed onboarding steps from user profile
 * @param {Object} userProfile - User profile object for additional checks
 * @returns {string} - Route to navigate to
 */
export const getOnboardingRoute = (completedSteps, userProfile = {}) => {
  // Handle if completedSteps is not an array (legacy support)
  if (!Array.isArray(completedSteps)) {
    // If it's a string, treat it as legacy single step
    if (typeof completedSteps === 'string') {
      // Skip subscription redirect, go to dashboard or QR
      if (completedSteps === ONBOARDING_STEPS.SUBSCRIPTION || completedSteps === ONBOARDING_STEPS.COMPLETED) {
        return "/dashboard";
      }
      return SIGNUP_ROUTES[completedSteps] || SIGNUP_ROUTES[ONBOARDING_STEPS.EMAIL_REGISTRATION];
    }
    // If no completed steps, start from email registration
    return SIGNUP_ROUTES[ONBOARDING_STEPS.EMAIL_REGISTRATION];
  }

  // Updated logic: Skip subscription verification step entirely
  // 0) If SUBSCRIPTION or COMPLETED already present, user should go to dashboard
  if (completedSteps.includes(ONBOARDING_STEPS.SUBSCRIPTION) || 
      completedSteps.some((s) => String(s).toLowerCase() === ONBOARDING_STEPS.COMPLETED)) {
    return "/dashboard";
  }
  
  // 1) If PHONE_VALIDATION is present, go directly to dashboard (skip subscription step)
  if (completedSteps.includes(ONBOARDING_STEPS.PHONE_VALIDATION)) {
    return "/dashboard";
  }
  
  // 2) If EMAIL_REGISTRATION is present but PHONE_VALIDATION is not, check if phone validation needed
  if (completedSteps.includes(ONBOARDING_STEPS.EMAIL_REGISTRATION) && !completedSteps.includes(ONBOARDING_STEPS.PHONE_VALIDATION)) {
    // Phone step is optional: require it ONLY if a phone exists and is explicitly unverified
    const { mobile, contact_verified } = userProfile || {};
    const hasPhone = Boolean(mobile?.contact);
    const isUnverified = contact_verified === false;
    if (hasPhone && isUnverified) {
      return SIGNUP_ROUTES[ONBOARDING_STEPS.PHONE_VALIDATION];
    }
    // Otherwise go directly to dashboard (skip subscription)
    return "/dashboard";
  }
  
  // 3) Otherwise start from EMAIL_REGISTRATION
  if (!completedSteps.includes(ONBOARDING_STEPS.EMAIL_REGISTRATION)) {
    return SIGNUP_ROUTES[ONBOARDING_STEPS.EMAIL_REGISTRATION];
  }
  
  // 4) Default to dashboard
  return "/dashboard";
};

/**
 * Check if user has completed onboarding
 * @param {Array|string} completedSteps - Array of completed steps or single step string
 * @returns {boolean}
 */
export const isOnboardingComplete = (completedSteps) => {
  if (Array.isArray(completedSteps)) {
    const lower = completedSteps.map((s) => String(s).toLowerCase());
    return lower.includes(ONBOARDING_STEPS.SUBSCRIPTION) && lower.includes(ONBOARDING_STEPS.COMPLETED);
  }
  return false;
};

/**
 * Get the next onboarding step
 * @param {string} currentStep - Current onboarding step
 * @param {boolean} skipPhone - Whether to skip phone validation
 * @returns {string} - Next onboarding step
 */
export const getNextOnboardingStep = (currentStep) => {
  const stepOrder = [
    ONBOARDING_STEPS.EMAIL_REGISTRATION,
    ONBOARDING_STEPS.PHONE_VALIDATION,
    ONBOARDING_STEPS.SUBSCRIPTION,
    ONBOARDING_STEPS.COMPLETED,
  ];
  const currentIndex = stepOrder.indexOf(currentStep);
  if (currentIndex === -1 || currentIndex === stepOrder.length - 1) return ONBOARDING_STEPS.COMPLETED;
  return stepOrder[currentIndex + 1];
};
